import { SortEnumType } from '@/generated/graphql'
import { graphqlRequest } from '@/lib/serverSideClient'
import { getDefaultCategorySortSlug } from '../../../categoryConfig'

type CategoryProductCommercialResponse = {
  productsByCategorySlugCommercial?: {
    totalCount: number
    items?: Array<{
      id: number
      displayPrice?: number | null
      discountedDisplayPrice?: number | null
      options: Array<{
        id?: number | null
        description?: string | null
        price: number
        salePrice?: number | null
        priceIncVat?: number | null
        salePriceIncVat?: number | null
        taxType?: number | null
        inStock: boolean
        productType?: {
          id: number
          name?: string | null
        } | null
        currency?: {
          symbol?: string | null
          code?: string | null
        } | null
      }>
    }> | null
    pageInfo: {
      hasNextPage: boolean
      hasPreviousPage: boolean
    }
  } | null
}

type EcommProductContentsByIdsResponse = {
  ecommProductContentsByIds: Array<{
    id: number
    name?: string | null
    url?: string | null
    blurb?: string | null
    hasDownloads: boolean
    vanishingIncExclusive: boolean
    thumbnails?: {
      default?: string | null
      square?: string | null
      video?: string | null
    } | null
    productType?: {
      id: number
      name?: string | null
    } | null
    creators: Array<{
      id: number
      name?: string | null
      urlStub?: string | null
    }>
  }>
}

export type CategoryListingProduct = {
  id: number
  name?: string | null
  url?: string | null
  blurb?: string | null
  hasDownloads: boolean
  vanishingIncExclusive: boolean
  thumbnails?: {
    default?: string | null
    square?: string | null
    video?: string | null
  } | null
  productType?: {
    id: number
    name?: string | null
  } | null
  creators: Array<{
    id: number
    name?: string | null
    urlStub?: string | null
  }>
  displayPrice?: number | null
  discountedDisplayPrice?: number | null
  options: Array<{
    id?: number | null
    description?: string | null
    price: number
    salePrice?: number | null
    priceIncVat?: number | null
    salePriceIncVat?: number | null
    taxType?: number | null
    inStock: boolean
    productType?: {
      id: number
      name?: string | null
    } | null
    currency?: {
      symbol?: string | null
      code?: string | null
    } | null
  }>
}

export type CategoryProductsResult = {
  productsByCategorySlug: {
    totalCount: number
    items: CategoryListingProduct[]
    pageInfo: {
      hasNextPage: boolean
      hasPreviousPage: boolean
    }
  }
}

const CATEGORY_PRODUCTS_COMMERCIAL_QUERY = /* GraphQL */ `
  query CategoryProductsCommercial(
    $categorySlug: String!
    $take: Int!
    $skip: Int!
    $brandCode: String!
    $includeChildCategories: Boolean!
    $order: [ProductByCategorySort!]
    $where: ProductByCategoryFilter
    $productTypeGroup: String
  ) {
    productsByCategorySlugCommercial(
      skip: $skip
      take: $take
      brandCode: $brandCode
      includeChildCategories: $includeChildCategories
      categorySlug: $categorySlug
      where: $where
      order: $order
      productTypeGroup: $productTypeGroup
    ) {
      totalCount
      items {
        id
        displayPrice
        discountedDisplayPrice
        options {
          id
          description
          price
          salePrice
          priceIncVat
          salePriceIncVat
          taxType
          inStock
          productType {
            id
            name
          }
          currency {
            symbol
            code
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const ECOMM_PRODUCT_CONTENTS_BY_IDS_QUERY = /* GraphQL */ `
  query EcommProductContentsByIds($ids: [Int!]!, $maxBlurbLength: Int) {
    ecommProductContentsByIds(ids: $ids, maxBlurbLength: $maxBlurbLength) {
      id
      name
      url
      blurb
      hasDownloads
      vanishingIncExclusive
      thumbnails {
        default
        square
        video
      }
      productType {
        id
        name
      }
      creators {
        id
        name
        urlStub
      }
    }
  }
`

export const staticSlugsToRender = [
  {
    categorySlug: 'magic-books',
    productTypeFilter: 'all',
    slugs: ['newest', '1'],
  },
  {
    categorySlug: 'magic-books',
    productTypeFilter: 'all',
    slugs: ['a-to-z', '1'],
  },
  {
    categorySlug: 'magic-books',
    productTypeFilter: 'all',
    slugs: ['price-asc', '1'],
  },
  {
    categorySlug: 'magic-books',
    productTypeFilter: 'all',
    slugs: ['price-desc', '1'],
  },
  {
    categorySlug: 'magic-books',
    productTypeFilter: 'all',
    slugs: ['best-sellers', '1'],
  },
  {
    categorySlug: 'magic-books',
    productTypeFilter: 'all',
    slugs: ['rating', '1'],
  },
]

export const translateSort = (sort: string) => {
  switch (sort) {
    case 'newest':
      return { dateAdded: SortEnumType.Desc }
    case 'a-to-z':
      return { name: SortEnumType.Asc }
    case 'price-asc':
      return { displayPrice: SortEnumType.Asc }
    case 'price-desc':
      return { displayPrice: SortEnumType.Desc }
    case 'biggest-discount':
      return { biggestDiscountPercentage: SortEnumType.Desc }
    case 'best-sellers':
      return { salesRank: SortEnumType.Asc }
    case 'rating':
      return { rating: SortEnumType.Desc }
    default:
      return { dateAdded: SortEnumType.Desc }
  }
}

const PRODUCT_TYPE_MAPPING = {
  apps: 'app',
  books: 'book',
  downloads: 'download',
  ebooks: 'ebook',
  tricks: 'trick',
  dvds: 'dvd',
} as const

export type ProductTypeUrlKey = keyof typeof PRODUCT_TYPE_MAPPING

const safeDecodeURIComponent = (value: string) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/** Parsed, sorted, unique URL keys (`books`, `tricks`, …). Empty means no type filter (`all`). */
export const parseProductTypeFilterUrlKeys = (
  productTypeFilter: string
): ProductTypeUrlKey[] => {
  const raw = safeDecodeURIComponent(productTypeFilter.trim().toLowerCase())
  if (!raw || raw === 'all') {
    return []
  }

  // Split on +, comma, or whitespace. '+' in path segments is often a space
  // by the time Next passes [productTypeFilter], so treat spaces like separators.
  const parts = raw
    .split(/[,+\s]+/)
    .map(part => part.trim().toLowerCase())
    .filter(Boolean)

  const keys = new Set<ProductTypeUrlKey>()
  for (const part of parts) {
    if (part in PRODUCT_TYPE_MAPPING) {
      keys.add(part as ProductTypeUrlKey)
    }
  }

  return [...keys].sort((a, b) => a.localeCompare(b))
}

/** Canonical segment for URLs and metadata: `all`, a single key, or `books+tricks`. */
export const canonicalProductTypeFilterSegment = (
  productTypeFilter: string
): string => {
  const keys = parseProductTypeFilterUrlKeys(productTypeFilter)
  if (keys.length === 0) {
    return 'all'
  }
  if (keys.length === 1) {
    return keys[0]!
  }
  return keys.join('+')
}

/**
 * Canonical segment from selected keys limited to what the category allows.
 * Empty selection or “every enabled type” maps to `all`.
 */
export const canonicalProductTypeFilterSegmentFromSelection = (
  selectedKeys: ProductTypeUrlKey[],
  enabledKeys: readonly ProductTypeUrlKey[]
): string => {
  const enabledSet = new Set(enabledKeys)
  const picked = [
    ...new Set(
      selectedKeys.filter((k): k is ProductTypeUrlKey => enabledSet.has(k))
    ),
  ].sort((a, b) => a.localeCompare(b))

  if (picked.length === 0) {
    return 'all'
  }

  const enabledSorted = [...enabledKeys].sort((a, b) => a.localeCompare(b))
  if (
    picked.length === enabledSorted.length &&
    picked.every((value, index) => value === enabledSorted[index])
  ) {
    return 'all'
  }

  if (picked.length === 1) {
    return picked[0]!
  }

  return picked.join('+')
}

const mapProductTypeFiltersToApiGroupString = (
  urlKeys: ProductTypeUrlKey[]
): string | undefined => {
  if (urlKeys.length === 0) {
    return undefined
  }
  const apiTokens = [
    ...new Set(urlKeys.map(key => PRODUCT_TYPE_MAPPING[key])),
  ].sort((a, b) => a.localeCompare(b))
  return apiTokens.join(',')
}

/** Short label for counts and filter copy (e.g. `products`, `Books`, `Books and tricks`). */
export const formatProductTypeFilterSummary = (
  productTypeFilter: string
): string => {
  const keys = parseProductTypeFilterUrlKeys(productTypeFilter)
  if (keys.length === 0) {
    return 'products'
  }
  const parts = keys.map(key =>
    key === 'dvds' ? 'DVDs' : key.charAt(0).toUpperCase() + key.slice(1)
  )
  if (parts.length === 1) {
    return parts[0]!
  }
  if (parts.length === 2) {
    return `${parts[0]} and ${parts[1]}`
  }
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`
}

export interface ParsedUrlFilters {
  filters: string[]
  sort: string
  page: number
  inStock: boolean
  onSale: boolean
  exclusive: boolean
}

const CATEGORY_SORT_SLUGS = new Set([
  'newest',
  'a-to-z',
  'price-asc',
  'price-desc',
  'biggest-discount',
  'best-sellers',
  'rating',
])

const CATEGORY_FILTER_SLUGS = new Set(['in-stock', 'on-sale', 'exclusives'])

const POSITIVE_INTEGER_SEGMENT = /^[1-9]\d*$/

export const isValidProductTypeFilterSegment = (
  productTypeFilter: string
): boolean => {
  const raw = safeDecodeURIComponent(productTypeFilter.trim().toLowerCase())
  if (!raw || raw === 'all') {
    return true
  }

  const parts = raw
    .split(/[,+\s]+/)
    .map(part => part.trim().toLowerCase())
    .filter(Boolean)

  return parts.length > 0 && parts.every(part => part in PRODUCT_TYPE_MAPPING)
}

export const isValidCategoryListingSlugs = (
  slugs: string[] | undefined
): boolean => {
  const slugList = slugs ?? []
  if (slugList.length === 0) {
    return true
  }

  if (slugList.length < 2) {
    return false
  }

  const sort = slugList[slugList.length - 2]
  const page = slugList[slugList.length - 1]
  const filters = slugList.slice(0, -2)

  return (
    Boolean(sort && CATEGORY_SORT_SLUGS.has(sort)) &&
    Boolean(page && POSITIVE_INTEGER_SEGMENT.test(page)) &&
    filters.every(filter => CATEGORY_FILTER_SLUGS.has(filter))
  )
}

export const isValidCategoryRouteState = ({
  productTypeFilter,
  slugs,
}: {
  categorySlug: string
  productTypeFilter: string
  slugs?: string[]
}): boolean => {
  return (
    isValidProductTypeFilterSegment(productTypeFilter) &&
    isValidCategoryListingSlugs(slugs)
  )
}

const parsePositivePageNumber = (value: string | undefined): number => {
  const page = Number.parseInt(value || '1', 10)
  return Number.isFinite(page) && page > 0 ? page : 1
}

const normalizeCategorySortSlug = (
  value: string | undefined,
  defaultSort: string
): string => {
  return value && CATEGORY_SORT_SLUGS.has(value) ? value : defaultSort
}

export const parseUrlFilters = (
  slugs: string[],
  categorySlug: string
): ParsedUrlFilters => {
  const defaultSort = getDefaultCategorySortSlug(categorySlug)
  const filters = slugs.length > 2 ? slugs.slice(0, -2) : []
  const sort = normalizeCategorySortSlug(slugs[slugs.length - 2], defaultSort)
  const page = parsePositivePageNumber(slugs[slugs.length - 1])

  return {
    filters,
    sort,
    page,
    inStock: filters.includes('in-stock'),
    onSale: filters.includes('on-sale'),
    exclusive: filters.includes('exclusives'),
  }
}

export const buildFilterUrl = (
  categorySlug: string,
  productTypeFilter: string,
  filters: string[],
  sort: string,
  page: number = 1
): string => {
  const filtersPath = filters.length > 0 ? `${filters.join('/')}/` : ''
  return `/${categorySlug}/${productTypeFilter}/${filtersPath}${sort}/${page}`
}

/**
 * Returns the canonical path for category listings.
 * Page-one default states use short URLs (e.g. /magic-books/).
 */
export const buildCategoryCanonicalPath = (
  categorySlug: string,
  productTypeFilter: string,
  filters: string[],
  sort: string,
  page: number
): string => {
  const segment = canonicalProductTypeFilterSegment(productTypeFilter)
  const defaultSort = getDefaultCategorySortSlug(categorySlug)
  const normalizedSort = sort || defaultSort
  const hasFilters = filters.length > 0
  const isFirstPage = page === 1
  const isDefaultSort = normalizedSort === defaultSort

  if (isFirstPage && !hasFilters && isDefaultSort) {
    if (segment === 'all') {
      return `/${categorySlug}/`
    }
    return `/${categorySlug}/${segment}/`
  }

  return `${buildFilterUrl(categorySlug, segment, filters, normalizedSort, page).replace(/\/?$/, '/')}`
}

export const getProducts = async (
  brandCode: string,
  categorySlug: string,
  productTypeFilter: string,
  inStock: boolean,
  onSale: boolean,
  exclusive: boolean,
  page: number,
  sort: any,
  perPage: number
): Promise<CategoryProductsResult> => {
  const urlKeys = parseProductTypeFilterUrlKeys(productTypeFilter)
  const productTypeGroup = mapProductTypeFiltersToApiGroupString(urlKeys)
  const order = [
    ...(inStock ? [{ inStock: SortEnumType.Desc }] : []),
    ...(Array.isArray(sort) ? sort : sort ? [sort] : []),
  ]

  const commercial = await graphqlRequest<CategoryProductCommercialResponse>(
    CATEGORY_PRODUCTS_COMMERCIAL_QUERY,
    {
      take: perPage,
      skip: (page - 1) * perPage,
      brandCode,
      includeChildCategories: true,
      categorySlug: categorySlug,
      order: order.length > 0 ? order : undefined,
      where: {
        ...(onSale && { onSale: { eq: true } }),
        ...(exclusive && { vanishingIncExclusive: { eq: true } }),
      },
      ...(productTypeGroup && { productTypeGroup }),
    }
  )

  const commercialItems =
    commercial.productsByCategorySlugCommercial?.items ?? []
  const productIds = commercialItems.map(product => product.id)

  if (productIds.length === 0) {
    return {
      productsByCategorySlug: {
        totalCount:
          commercial.productsByCategorySlugCommercial?.totalCount || 0,
        items: [],
        pageInfo: {
          hasNextPage:
            commercial.productsByCategorySlugCommercial?.pageInfo.hasNextPage ||
            false,
          hasPreviousPage:
            commercial.productsByCategorySlugCommercial?.pageInfo
              .hasPreviousPage || false,
        },
      },
    }
  }

  const content = await graphqlRequest<EcommProductContentsByIdsResponse>(
    ECOMM_PRODUCT_CONTENTS_BY_IDS_QUERY,
    {
      ids: productIds,
      maxBlurbLength: 300,
    }
  )

  const contentById = new Map(
    content.ecommProductContentsByIds.map(product => [product.id, product])
  )

  const mergedItems: CategoryListingProduct[] = []

  for (const product of commercialItems) {
    const contentItem = contentById.get(product.id)
    if (!contentItem) {
      continue
    }

    mergedItems.push({
      ...contentItem,
      displayPrice: product.displayPrice,
      discountedDisplayPrice: product.discountedDisplayPrice,
      options: product.options || [],
    })
  }

  return {
    productsByCategorySlug: {
      totalCount: commercial.productsByCategorySlugCommercial?.totalCount || 0,
      items: mergedItems,
      pageInfo: {
        hasNextPage:
          commercial.productsByCategorySlugCommercial?.pageInfo.hasNextPage ||
          false,
        hasPreviousPage:
          commercial.productsByCategorySlugCommercial?.pageInfo
            .hasPreviousPage || false,
      },
    },
  }
}

const TOP_SELLERS_SIDEBAR_DEFAULT_LIMIT = 5
const TOP_SELLERS_FETCH_SIZE = 20

/**
 * Best-selling products in this category (by global SalesRank),
 * respecting the same filters and product-type segment as the main listing.
 */
export const getCategoryTopSellers = async (
  brandCode: string,
  categorySlug: string,
  productTypeFilter: string,
  inStock: boolean,
  onSale: boolean,
  exclusive: boolean,
  limit: number = TOP_SELLERS_SIDEBAR_DEFAULT_LIMIT
): Promise<CategoryListingProduct[]> => {
  const urlKeys = parseProductTypeFilterUrlKeys(productTypeFilter)
  const productTypeGroup = mapProductTypeFiltersToApiGroupString(urlKeys)
  const order = [
    ...(inStock ? [{ inStock: SortEnumType.Desc }] : []),
    { salesRank: SortEnumType.Asc },
  ]

  const commercialItems: NonNullable<
    NonNullable<
      CategoryProductCommercialResponse['productsByCategorySlugCommercial']
    >['items']
  > = []
  const take = Math.min(100, Math.max(TOP_SELLERS_FETCH_SIZE, limit))
  let skip = 0
  let hasNextPage = true

  // Filtering on the projected option prices makes SQL evaluate the price
  // subqueries for the whole category before applying the best-seller limit.
  // Page by SalesRank first, then apply the same paid-product test to the
  // small result set returned by the commercial query.
  while (commercialItems.length < limit && hasNextPage) {
    const commercial = await graphqlRequest<CategoryProductCommercialResponse>(
      CATEGORY_PRODUCTS_COMMERCIAL_QUERY,
      {
        take,
        skip,
        brandCode,
        includeChildCategories: true,
        categorySlug,
        order,
        where: {
          ...(onSale && { onSale: { eq: true } }),
          ...(exclusive && { vanishingIncExclusive: { eq: true } }),
        },
        ...(productTypeGroup && { productTypeGroup }),
      }
    )

    const page = commercial.productsByCategorySlugCommercial
    const items = page?.items ?? []
    commercialItems.push(
      ...items.filter(
        product =>
          (product.displayPrice != null && product.displayPrice > 0) ||
          product.options?.some(
            option => option.price > 0 || (option.salePrice ?? 0) > 0
          )
      )
    )
    skip += items.length
    hasNextPage = Boolean(page?.pageInfo.hasNextPage && items.length > 0)
  }

  commercialItems.length = Math.min(commercialItems.length, limit)
  const productIds = commercialItems.map(product => product.id)

  if (productIds.length === 0) {
    return []
  }

  const content = await graphqlRequest<EcommProductContentsByIdsResponse>(
    ECOMM_PRODUCT_CONTENTS_BY_IDS_QUERY,
    {
      ids: productIds,
      maxBlurbLength: 0,
    }
  )

  const contentById = new Map(
    content.ecommProductContentsByIds.map(product => [product.id, product])
  )

  const mergedItems: CategoryListingProduct[] = []

  for (const product of commercialItems) {
    const contentItem = contentById.get(product.id)
    if (!contentItem) {
      continue
    }

    mergedItems.push({
      ...contentItem,
      displayPrice: product.displayPrice,
      discountedDisplayPrice: product.discountedDisplayPrice,
      options: product.options || [],
    })
  }

  return mergedItems
}
